/**
 * receipt.js — 80mm termal chek uchun umumiy yordamchilar.
 *
 * Savdo cheki (Chiqim/index.vue) bilan bir xil ko'rinishda bo'lishi uchun CSS va
 * sarlavha shu yerda saqlanadi. Qaytarish cheklari (Qaytarishlar ro'yxati va
 * savdodagi "faqat qaytarilgan" rejimi) shu funksiyalardan foydalanadi.
 */

export function formatMoney(v) {
  const n = Number(v || 0);
  if (!n) return '0';
  return n.toLocaleString('ru-RU', { maximumFractionDigits: 2 }).replace(/,/g, ' ');
}

export function formatDateTime(ts) {
  if (!ts) return '';
  const d = new Date(Number(ts) * 1000);
  return d.toLocaleDateString('ru-RU') + ' ' + d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Mahsulot o'lchov birligini chek uchun qisqartiradi:
 * "Килограмм" → кг, "Квадратный метр" → м², "Штука" → шт.
 * Birlik ko'rsatilmagan bo'lsa — "шт" (eski xatti-harakat).
 */
const UNIT_RULES = [
  [/(квадрат|кв\.?\s*м|м2|м²|kvadrat)/, 'м²'],
  [/(кубик|кубическ|куб\.?\s*м|м3|м³|kubik)/, 'м³'],
  [/^(шт|штук|дона|dona|pcs|piece)/, 'шт'],
  [/^(кг|килограмм|kg|kilogramm)/, 'кг'],
  [/^(гр?$|грамм|gramm)/, 'г'],
  [/^(тонна|тн|tonna)/, 'т'],
  [/^(литр|л$|litr)/, 'л'],
  [/^(метр|м$|metr|погон)/, 'м'],
  [/^(упак|упаковка|пачка|пачк|paket|блок)/, 'упак'],
  [/^(рулон|rulon)/, 'рулон'],
  [/^(компл|набор|to'plam|toplam)/, 'компл'],
];

export function unitShort(name, fallback = 'шт') {
  const raw = String(name || '').trim();
  if (!raw) return fallback;
  const n = raw.toLowerCase();
  for (const [re, short] of UNIT_RULES) {
    if (re.test(n)) return short;
  }
  return raw.length <= 6 ? raw : raw.slice(0, 6) + '.';
}

/** Sklad chek sozlamalari: firma nomi, telefon, logotip, chek so'zi. */
export async function fetchSkladSettings(http) {
  const settings = { firmName: '', phone: '', checkWord: '', logoUrl: '' };
  const sklad_id = localStorage.getItem('sklad_id');
  if (!sklad_id) return settings;
  try {
    const [skladRes, settingsRes] = await Promise.all([
      http.get(`api/v1/sklads/id/${sklad_id}`),
      http.post('api/v1/sklad-settings', { sklad_id, key: 'a4_printer' }),
    ]);
    const sklad = (skladRes.data && skladRes.data.data) || skladRes.data || {};
    settings.phone = sklad.phone_number || '';
    settings.checkWord = sklad.check_word || '';
    settings.firmName = sklad.name || '';
    const printerData = settingsRes.data && settingsRes.data.data;
    if (printerData) {
      const parsed = JSON.parse(printerData);
      settings.firmName = (parsed && parsed.sender && parsed.sender.firm_name) || sklad.name || '';
      if (parsed && parsed.file_name) {
        settings.logoUrl = (http.defaults.baseURL || '') + 'api/v1/uploads/' + parsed.file_name;
      }
    }
  } catch (e) {
    // sozlama o'qilmasa ham chek chiqaverishi kerak
  }
  return settings;
}

/** Savdo cheki bilan bir xil termal CSS (+ qaytarish bloklari uchun sinflar). */
export const RECEIPT_CSS = `
  @page { size: 80mm 5000mm; margin: 0; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html, body { height: auto; }
  body {
    width: 80mm; font-family: 'Arial Narrow', 'Arial', 'Helvetica', sans-serif;
    font-size: 13px; color: #000; padding: 1.5mm 1mm;
    -webkit-font-smoothing: antialiased; line-height: 1.2;
    page-break-inside: avoid; break-inside: avoid;
  }
  .receipt-header, .receipt-info, .item-block, .calc-section, .debt-section,
  .receipt-footer, .receipt-barcode, .leader-line, .refund-section {
    page-break-inside: avoid; break-inside: avoid;
  }
  .receipt-logo { display: block; max-width: 90mm; max-height: 33mm; width: auto; height: auto; margin: 0 auto 2mm; object-fit: contain; }
  .receipt-header { text-align: center; margin-bottom: 2mm; }
  .firm-name { font-size: 18px; font-weight: 900; letter-spacing: 0.5px; text-transform: uppercase; }
  .firm-phone { font-size: 12px; margin-top: 1mm; word-break: break-all; }
  .receipt-date { font-size: 12px; color: #000; margin-top: 1mm; }
  .doc-title {
    font-size: 15px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.5px;
    margin-top: 1.5mm; padding: 1mm 0; border-top: 2px solid #000; border-bottom: 2px solid #000;
  }
  .receipt-divider { border-top: 1.5px dashed #000; margin: 2mm 0; }
  .receipt-info { margin: 1.5mm 0; }
  .info-row { display: flex; justify-content: space-between; font-size: 13px; line-height: 1.3; gap: 4px; }
  .info-label { color: #000; font-weight: 700; white-space: nowrap; }
  .info-value { font-weight: 700; text-align: right; word-break: break-word; }
  .items-section { margin: 1.5mm 0; }
  .item-block { padding: 1.2mm 0; border-top: 1px dashed #000; }
  .item-block:last-child { border-bottom: 1px dashed #000; }
  .page-break { page-break-after: always; break-after: page; }
  .ir-name {
    font-family: 'Arial Narrow', 'Arial', 'Helvetica', sans-serif;
    font-size: 13px; font-weight: 700; letter-spacing: -0.1px;
    word-break: break-word; white-space: normal; margin-bottom: 0.5mm; line-height: 1.1;
  }
  .leader-line { display: flex; align-items: baseline; font-size: 13px; line-height: 1.25; gap: 1px; width: 100%; }
  .ll-left { white-space: nowrap; }
  .ll-right { white-space: nowrap; font-weight: 700; text-align: right; }
  .ll-dots { flex: 1 1 auto; min-width: 4mm; border-bottom: 2px dotted #000; align-self: center; height: 0; margin: 0 1.5mm; }
  .ll-jami { font-size: 16px; font-weight: 900; border-top: 2px solid #000; border-bottom: 2px solid #000; padding: 0.8mm 0; margin: 0.8mm 0; }
  .ll-bold .ll-left, .ll-bold .ll-right { font-weight: 900; }
  .calc-section { margin: 1.5mm 0; }
  /* Qaytarish bloklari */
  .refund-section { margin-top: 2mm; padding-top: 1.5mm; border-top: 2px dashed #000; }
  .refund-title { font-size: 14px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 1mm; }
  .refund-when { font-size: 11px; font-style: italic; margin: 1mm 0 0.5mm; }
  .refund-note { font-size: 12px; font-weight: 700; border: 1.5px solid #000; padding: 0.6mm 1.5mm; display: inline-block; margin-top: 1mm; }
  .refund-comment { font-size: 12px; font-style: italic; margin-top: 1mm; word-break: break-word; }
  .receipt-footer { text-align: center; margin-top: 2mm; padding-bottom: 3mm; }
  .check-word { font-weight: 800; font-size: 13px; text-transform: uppercase; letter-spacing: 0.5px; }
  .receipt-barcode { text-align: center; margin-top: 2mm; padding-bottom: 3mm; }
  .receipt-barcode svg { width: 70mm; height: auto; max-height: 18mm; }
`;

/** Chek sarlavhasi (logo + firma + sana + hujjat turi). */
export function receiptHeaderHtml(settings, dateStr, docTitle) {
  const s = settings || {};
  return `
  <div class="receipt-header">
    ${s.logoUrl ? `<img src="${s.logoUrl}" class="receipt-logo" alt="logo" />` : ''}
    <div class="firm-name">${s.firmName || ''}</div>
    ${s.phone ? `<div class="firm-phone">Kontaktlar: ${s.phone}</div>` : ''}
    <div class="receipt-date">${dateStr || ''}</div>
    ${docTitle ? `<div class="doc-title">${docTitle}</div>` : ''}
  </div>`;
}

/** Bitta qator: "3 кг x 12 000 ..... 36 000 UZS" (birlik mahsulotdan olinadi) */
export function itemBlockHtml(row, index) {
  const qty = Number(row.count || row.qty || 0);
  const price = Number(row.price || 0);
  const summa = Number(row.summa != null ? row.summa : qty * price);
  const unit = unitShort(row.unit);
  return `
    <div class="item-block">
      <div class="ir-name">${index + 1}. ${row.name || '-'}</div>
      <div class="leader-line">
        <span class="ll-left">${formatMoney(qty)} ${unit} x ${formatMoney(price)}</span>
        <span class="ll-dots"></span>
        <span class="ll-right">${formatMoney(summa)} UZS</span>
      </div>
    </div>`;
}

/** Chekni yangi oynada ochib, printerga yuboradi. */
export function openReceiptWindow(title, bodyHtml) {
  const win = window.open('', '_blank', 'width=420,height=700');
  if (!win) {
    alert("Chop etish oynasi bloklandi — brauzerda pop-up'ga ruxsat bering.");
    return;
  }
  win.document.write(`<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${title}</title>
<style>${RECEIPT_CSS}</style>
</head>
<body>
${bodyHtml}
</body>
</html>`);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); win.close(); }, 400);
}
